<section class="mt-28 ml-28 mr-8 mb-10">
  <header class="flex items-center gap-4 mb-6">
    <img [src]="images.arrowLeft" alt="back" class="h-6 w-6 cursor-pointer" (click)="back()" />
    <h1 class="font-bold text-[22px] text-primary-purple">{{ literalService.get('services.routes.reorganize.title', true) }}</h1>
    <span class="text-neutrals-grey-04">{{ serviceInfo?.tag }}</span>
  </header>

  @if (loading && !simulation) {
    <div class="flex justify-center py-10">
      <img [src]="images.loading" alt="loading" class="w-10 h-10 animate-spin" />
    </div>
  } @else if (!summary) {
    <article class="bg-white rounded-lg p-6 flex flex-col gap-6">
      <div class="flex gap-4">
        @for (option of [RouteReorganizationType.MERGE, RouteReorganizationType.SPLIT]; track option) {
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="reorganizationType" class="radio radio-sm" [checked]="type === option" (change)="changeType(option)" />
            <span class="font-semibold">{{ literalService.get('services.routes.reorganize.type.' + option, true) }}</span>
          </label>
        }
      </div>
      <p class="text-sm text-neutrals-grey-04">{{ literalService.get('services.routes.reorganize.type.' + type + 'Hint', true) }}</p>

      <div class="border border-neutrals-grey-02 rounded-lg p-4">
        <p class="font-semibold mb-2">{{ literalService.get('services.routes.reorganize.sources.' + type, true) }}</p>
        @for (route of routes; track route.id) {
          <label class="flex items-center gap-2 py-1 cursor-pointer">
            <input type="checkbox" class="checkbox checkbox-sm" [checked]="selectedRouteIds.includes(route.id!)" (change)="toggleRoute(route.id!)" />
            <span>{{ route.name }}</span>
            @if (!route.active) {
              <span class="badge badge-ghost badge-sm">{{ literalService.get('services.routes.reorganize.inactive', true) }}</span>
            }
          </label>
        }
      </div>
      @if (type === RouteReorganizationType.SPLIT && selectedRouteIds.length) {
        <label class="flex items-center gap-4">
          <span class="font-semibold">{{ literalService.get('services.routes.reorganize.routesCount', true) }}</span>
          <input type="number" class="input input-sm w-24" [min]="APP.REORGANIZATION_MIN_SPLIT_ROUTES" [max]="maxRoutesCount" [ngModel]="routesCount" (ngModelChange)="changeRoutesCount($event)" />
        </label>
      }
      <p class="text-sm alert alert-info">{{ literalService.get('services.routes.reorganize.sources.hint', true) }}</p>
      <app-wizard-step-buttons />
    </article>
  } @else {
    <article class="bg-white rounded-lg p-6 flex flex-col gap-6">
      <div class="grid gap-4" [ngClass]="newRoutes.length > 1 ? 'grid-cols-2' : 'grid-cols-1'">
        @for (newRoute of newRoutes; track $index; let newRouteIndex = $index) {
          <div class="border border-neutrals-grey-02 rounded-lg p-4 flex flex-col gap-1">
            <span class="font-bold">{{ newRoute.name }}</span>
            <span class="text-sm">{{ literalService.get('services.routes.reorganize.summary.maxTime', true, { minutes: newRoute.maxTime }) }}</span>
            <span class="text-sm">{{ literalService.get('services.routes.reorganize.summary.departures', true, { count: departuresCount(setups[newRouteIndex]) }) }}</span>
            @if (missingCrew(setups[newRouteIndex])) {
              <span class="text-sm text-error">{{ literalService.get('services.routes.reorganize.summary.missingCrew', true) }}</span>
            }
            @switch (windowIssue(newRouteIndex)) {
              @case (ScheduleWindowIssue.BELOW_MIN_PATH_TIME) {
                <span class="text-sm text-error">{{ literalService.get('2013_ERROR.text', true) }}</span>
              }
              @case (ScheduleWindowIssue.BELOW_MAX_PATH_TIME) {
                <span class="text-sm text-warning">{{ literalService.get('setup.windowIssue.belowMaxPathTime', true, { minutes: newRouteMaxPathTime(newRouteIndex) }) }}</span>
              }
            }
          </div>
        }
        <p class="text-sm text-neutrals-grey-04">{{ literalService.get('services.routes.reorganize.summary.sourcesDeactivated', true, { count: draft?.sourceSchedules?.length }) }}</p>
      </div>

      <div class="flex items-center justify-between">
        <h2 class="font-bold">{{ literalService.get('services.routes.reorganize.bookings.title', true) }} ({{ bookings.length }})</h2>
        <button type="button" class="secondary p-2 px-4 border-2 font-semibold uppercase cursor-pointer" [disabled]="loading" (click)="autoAssign()">{{ literalService.get('services.routes.reorganize.bookings.autoAssign', true) }}</button>
      </div>
      @if (simulation?.unplannedBookingIds?.length) {
        <div class="alert alert-warning">{{ literalService.get('services.routes.reorganize.bookings.unplanned', true, { count: simulation!.unplannedBookingIds.length }) }}</div>
      }
      <div class="border border-neutrals-grey-02 rounded-lg">
        @for (booking of bookings; track booking.bookingId) {
          <div class="grid grid-cols-12 items-center gap-2 px-4 py-2 border-b border-neutrals-grey-02 last:border-b-0">
            <span class="col-span-2 truncate">{{ bookingName(booking) }}</span>
            <span class="col-span-3 text-sm truncate">{{ sourceRouteName(booking.sourceRouteId) }} · {{ formatDateTime(booking.sourceDepartureDateTime) }}</span>
            <span class="col-span-3 text-sm truncate">{{ booking.booking.origin.name }} → {{ booking.booking.destination.name }}</span>
            <span class="col-span-1 text-sm">{{ booking.seats }}@if (booking.prmSeats) { · PMR {{ booking.prmSeats }} }</span>
            <div class="col-span-3 flex flex-col">
              <select class="select select-sm w-full" [ngClass]="{ 'select-error': !bookingSelection(booking) || booking.errorCode }" [disabled]="loading" [ngModel]="bookingSelection(booking)" (ngModelChange)="selectBookingTarget(booking, $event)">
                <option [ngValue]="undefined" disabled>{{ literalService.get('services.routes.reorganize.bookings.choose', true) }}</option>
                @for (option of departureOptions(booking); track optionKey(option)) {
                  <option [ngValue]="optionKey(option)">{{ optionLabel(option) }}</option>
                }
                <option [ngValue]="CANCEL_BOOKING">{{ literalService.get('services.routes.reorganize.bookings.cancel', true) }}</option>
              </select>
              @if (booking.errorCode) {
                <span class="text-xs text-error">{{ literalService.get(booking.errorCode + '_ERROR.title', true) }}</span>
              } @else if (!booking.compatibleNewRouteIndexes.length) {
                <span class="text-xs text-error">{{ literalService.get('services.routes.reorganize.bookings.incompatible', true) }}</span>
              }
            </div>
          </div>
        }
        @if (!bookings.length) {
          <p class="px-4 py-2 text-neutrals-grey-04">{{ literalService.get('services.routes.reorganize.bookings.empty', true) }}</p>
        }
      </div>

      @if (simulation?.departures?.length) {
        <table class="table">
          <thead>
            <tr>
              <th>{{ literalService.get('services.routes.reorganize.summary.departure', true) }}</th>
              <th>{{ literalService.get('services.routes.reorganize.summary.seats', true) }}</th>
              <th>PMR</th>
            </tr>
          </thead>
          <tbody>
            @for (departure of simulation!.departures; track departure.newRouteIndex + departure.departureDateTime) {
              <tr [ngClass]="{ 'bg-error/10': isOverCapacity(departure) }">
                <td>{{ routeName(departure.newRouteIndex) }} · {{ formatDateTime(departure.departureDateTime) }}</td>
                <td [ngClass]="{ 'text-error font-bold': departure.plannedSeats > departure.seatsCapacity }">{{ departure.plannedSeats }} / {{ departure.seatsCapacity }}</td>
                <td [ngClass]="{ 'text-error font-bold': departure.plannedPrmSeats > departure.prmCapacity }">{{ departure.plannedPrmSeats }} / {{ departure.prmCapacity }}</td>
              </tr>
            }
          </tbody>
        </table>
      }

      <section class="flex flex-col gap-4 max-w-xl">
        @if (draft?.cancellations?.length) {
          <div class="flex flex-col gap-4 border border-neutrals-grey-02 rounded-lg p-4">
            <p class="font-bold">{{ literalService.get('services.routes.reorganize.summary.cancellations', true, { count: draft!.cancellations.length }) }}</p>
            @if (draft!.ticketing) {
              <div class="flex flex-col gap-2">
                <label>{{ literalService.get('services.routes.design.update.review.refund.title', true) }}</label>
                @for (refund of refundOptions; track refund) {
                  <div class="flex items-center gap-1">
                    <input [id]="'reorganization-' + refund" type="radio" class="radio radio-xs" name="reorganization-refund" [checked]="refundOption === refund" (change)="refundOption = refund" />
                    <label [for]="'reorganization-' + refund" class="cursor-pointer">{{ literalService.get('services.routes.design.update.review.refund.' + refund, true) }}</label>
                  </div>
                }
              </div>
            }
            @if (draft!.roundTrip) {
              <div class="flex flex-row gap-4">
                <app-toggle [id]="'reorganizationCancelRelated'" [checked]="draft!.cancellationOptions.cancelRelated" (change)="draft!.cancellationOptions.cancelRelated = !draft!.cancellationOptions.cancelRelated"></app-toggle>
                <label>{{ literalService.get('services.routes.design.update.review.related.delete', true) }}</label>
              </div>
            }
          </div>
        }
        @if (draft?.ticketing) {
          <div class="flex items-center justify-between">
            <label class="font-bold">{{ literalService.get('bookings.actions.editBooking.recomputePrice', true) }}</label>
            <app-toggle [id]="'recomputePrice'" [checked]="recomputePrice" (change)="recomputePrice = !recomputePrice"></app-toggle>
          </div>
        }
        <div class="flex items-center justify-between">
          <label class="font-bold">{{ literalService.get('bookings.actions.deleteBooking.notify', true) }}</label>
          <app-toggle [id]="'notify'" [checked]="draft!.sendNotification" (change)="draft!.sendNotification = !draft!.sendNotification"></app-toggle>
        </div>
        @if (restrictionWarnings.length) {
          <label class="flex items-center gap-2 cursor-pointer alert alert-warning">
            <input type="checkbox" class="checkbox checkbox-sm" [(ngModel)]="restrictionsAccepted" />
            <span>{{ literalService.get('services.routes.reorganize.summary.acceptRestrictions', true, { count: restrictionWarnings.length }) }}</span>
          </label>
        }
        @if (applying) {
          <progress class="progress progress-primary w-full" [value]="progressPercentage" max="100"></progress>
          <p class="text-sm text-neutrals-grey-04">{{ progressDetail }}</p>
        }
      </section>
      <app-wizard-step-buttons />
    </article>
  }
</section>
